import { AfriexCountryCode, AfriexCurrencyCode, AfriexErrorCode, AfriexTransactionChannel, AfriexTransactionProcessor, AfriexTransactionType, } from '../enums/index.js';
import { BusinessFeeComparator, BusinessLimitField, BusinessLimitFrequency, } from '../types/index.js';
import { AfriexError } from './AfriexError.js';
const LIMIT_TYPES = ['AMOUNT', 'COUNT'];
// Browser-safe MongoDB ObjectId shape check. Core must not depend on
// `mongoose` (server-only), so business IDs are validated by structure here.
const OBJECT_ID_REGEX = /^[a-fA-F0-9]{24}$/;
/**
 * Shape validator for a {@link BusinessLimit} create/update request, shared by
 * the admin dashboard (client-side pre-submit) and the server (before persist).
 * Throws {@link AfriexError} with {@link AfriexErrorCode.InvalidBusinessLimitRequest}
 * and a human-readable `errorMessage` in the error meta.
 */
export class BusinessLimitRequestValidator {
    constructor(request, errorMeta = {}) {
        this.request = request;
        this.errorMeta = errorMeta;
    }
    throwErrorMessage(errorMessage) {
        throw new AfriexError(AfriexErrorCode.InvalidBusinessLimitRequest, {
            ...this.errorMeta,
            errorMessage,
        });
    }
    validateName() {
        if (!this.request?.name) {
            this.throwErrorMessage('No limit name provided');
        }
        return this;
    }
    validateCode() {
        const { code } = this.request;
        if (!code) {
            return this;
        }
        // The code keys `meta.limitStats` as a Mongo field path (`$inc`), so a dot
        // would nest the stat under the prefix instead of keying it.
        if (!/^[A-Za-z0-9_-]{1,64}$/.test(code)) {
            this.throwErrorMessage('Invalid limit code provided: use letters, numbers, underscores or hyphens');
        }
        return this;
    }
    validateCurrencyTag(value) {
        const currencies = value.split(',').map((v) => v.trim());
        currencies.forEach((currency) => {
            if (!Object.values(AfriexCurrencyCode).includes(currency)) {
                this.throwErrorMessage(`Invalid currency code provided for limit tag: ${currency}`);
            }
        });
    }
    validateChannelTag(value) {
        const channels = value.split(',').map((v) => v.trim());
        channels.forEach((channel) => {
            if (!Object.values(AfriexTransactionChannel).includes(channel)) {
                this.throwErrorMessage(`Invalid transaction channel provided for limit tag: ${channel}`);
            }
        });
    }
    validateProcessorTag(value) {
        const processors = value.split(',').map((v) => v.trim());
        processors.forEach((processor) => {
            if (!Object.values(AfriexTransactionProcessor).includes(processor)) {
                this.throwErrorMessage(`Invalid transaction processor provided for limit tag: ${processor}`);
            }
        });
    }
    validateCountryTag(value) {
        const countries = value.split(',').map((v) => v.trim());
        countries.forEach((country) => {
            if (!Object.values(AfriexCountryCode).includes(country)) {
                this.throwErrorMessage(`Invalid country code provided for limit tag: ${country}`);
            }
        });
    }
    validateBusinessTag(value) {
        const businessIds = value.split(',').map((v) => v.trim());
        businessIds.forEach((businessId) => {
            if (!OBJECT_ID_REGEX.test(businessId)) {
                this.throwErrorMessage(`Invalid business ID provided for limit tag: ${businessId}`);
            }
        });
    }
    validateTransactionTypeTag(value) {
        const types = value.split(',').map((v) => v.trim());
        types.forEach((type) => {
            if (!Object.values(AfriexTransactionType).includes(type)) {
                this.throwErrorMessage(`Invalid transaction type provided for limit tag: ${type}`);
            }
        });
    }
    validateTagField(field, value) {
        switch (field) {
            case BusinessLimitField.FromCurrency:
            case BusinessLimitField.ToCurrency:
                return this.validateCurrencyTag(value);
            case BusinessLimitField.FromCountry:
            case BusinessLimitField.ToCountry:
                return this.validateCountryTag(value);
            case BusinessLimitField.FromChannel:
            case BusinessLimitField.ToChannel:
                return this.validateChannelTag(value);
            case BusinessLimitField.FromProcessor:
            case BusinessLimitField.ToProcessor:
                return this.validateProcessorTag(value);
            case BusinessLimitField.Business:
                return this.validateBusinessTag(value);
            case BusinessLimitField.TransactionType:
                return this.validateTransactionTypeTag(value);
            // Rejected rather than validated: BusinessTransactionEntity's
            // generateBusinessLimitTags never emits these four, so the matcher
            // compares them against an empty value. That is wrong in both
            // directions. Most comparators cannot be satisfied, so the limit
            // silently never applies; and an empty numeric value coerces to 0, so a
            // LESS_THAN predicate is satisfied by every transaction and the limit
            // silently applies to all of them. Accepting them means storing a rule
            // that cannot mean what its author intended, so the request is refused
            // until the tag generator supplies these fields. The matcher's current
            // behaviour for rules already stored is pinned in
            // test/tests/domain/entities/BusinessLimitEntity.test.ts.
            case BusinessLimitField.TransactionCount:
            case BusinessLimitField.TransactionVolume:
            case BusinessLimitField.TransactionAmount:
            case BusinessLimitField.WalletTag:
                return this.throwErrorMessage(`Limit field ${field} is not supported as a filter: transactions are not tagged with it, so the limit would not apply as written. Remove this filter to save the limit.`);
            default:
                return this.throwErrorMessage(`Unknown limit field: ${String(field)}`);
        }
    }
    validateTags(tags) {
        if (!tags) {
            this.throwErrorMessage('Filters are required');
            return this;
        }
        // Required, not merely allowed: a limit with no type filter applies to
        // every transaction type, which is almost never what its author meant (a
        // daily deposit cap would also cap withdrawals and swaps).
        if (!tags[BusinessLimitField.TransactionType]) {
            this.throwErrorMessage(`A ${BusinessLimitField.TransactionType} filter is required so the limit only caps the transaction types it names`);
        }
        Object.entries(tags).forEach(([field, valueWithComparator]) => {
            if (!valueWithComparator) {
                this.throwErrorMessage('Filter value is missing for field: ' + field);
                return;
            }
            if (!Object.values(BusinessLimitField).includes(field)) {
                this.throwErrorMessage(`Invalid limit tag field: ${field}`);
            }
            const [comparator, value] = valueWithComparator.split('::');
            if (!Object.values(BusinessFeeComparator).includes(comparator)) {
                this.throwErrorMessage(`Invalid comparator provided for limit filter: ${field}: ${comparator}`);
            }
            this.validateTagField(field, value);
        });
        return this;
    }
    validateLimitEntry(frequency, entry) {
        if (!Object.values(BusinessLimitFrequency).includes(frequency)) {
            this.throwErrorMessage(`Invalid limit frequency: ${frequency}`);
        }
        if (!entry || !entry.type || !LIMIT_TYPES.includes(entry.type)) {
            this.throwErrorMessage(`Invalid limit type provided for ${frequency}`);
            return;
        }
        const { max, min } = entry.value ?? {};
        // An entry with no bound at all would never be enforceable.
        if (max === undefined && min === undefined) {
            this.throwErrorMessage(`Invalid limit value provided for ${frequency}`);
            return;
        }
        [max, min].forEach((bound) => {
            if (bound !== undefined &&
                (typeof bound !== 'number' || !Number.isFinite(bound) || bound < 0)) {
                this.throwErrorMessage(`Invalid limit value provided for ${frequency}`);
            }
        });
    }
    validateLimit() {
        const { limit } = this.request;
        if (!limit || !Object.keys(limit).length) {
            this.throwErrorMessage('At least one limit is required');
            return this;
        }
        Object.entries(limit).forEach(([frequency, entry]) => {
            this.validateLimitEntry(frequency, entry);
        });
        return this;
    }
    validateMetaFlags(flags) {
        if (!flags) {
            return this;
        }
        if (flags.isDeactivated !== undefined &&
            typeof flags.isDeactivated !== 'boolean') {
            this.throwErrorMessage('Invalid isDeactivated flag provided');
        }
        return this;
    }
    validateMeta() {
        const { meta } = this.request;
        // Not skipped when `meta` or `meta.tags` is absent: validateTags refuses a
        // missing filter set, so a limit cannot be saved without a type filter.
        this.validateTags(meta?.tags);
        if (meta?.flags) {
            this.validateMetaFlags(meta.flags);
        }
        return this;
    }
    validate() {
        this.validateName();
        this.validateCode();
        this.validateLimit();
        this.validateMeta();
    }
    value() {
        this.validate();
        return this.request;
    }
}
//# sourceMappingURL=BusinessLimitRequestValidator.js.map